/**
 * Drives the Signify product modal: distributor/product selection, price fetch,
 * mapping edits, validation, and insert.
 *
 * Mappings are resolved per response (`resolveSignifySourceMappings`), so
 * `sourceMappings` only ever lists paths the item actually returned — that is
 * what keeps an absent source from claiming a destination and registering a
 * false duplicate. `customMappings` overlays the resolved map, keyed by response
 * path, and is never re-resolved. All mapping config is per-org; none of it is
 * hardcoded.
 *
 * `duplicateMappings` can now only fire on a session override: the resolved map
 * holds each destination once, so inverting it cannot produce two paths claiming
 * one destination. A destination filled by a `constant` source has no response
 * row, so `constantMappings` reports it read-only and no session override can
 * reach it — the accepted limit of SYD-2424.
 *
 * `distributorSearchValue` is derived, not mirrored: `typedDistributorSearch` is
 * `null` whenever the user is not actively typing, and only then does the box
 * fall back to the selected distributor's name looked up in `allDistributors` at
 * read time. `''` is a real typed value — it keeps the box clearable — so the
 * fallback is `??`, never `||`. Selecting, opening and auto-populating reset the
 * typed value to `null` instead of writing a name into it, which is what keeps a
 * renamed distributor from showing stale text.
 */
import { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import { TableColumnType, type Fields, type EntityRecord } from '#types/System.types';
import {
  ProductSearchResult,
  DEFAULT_SIGNIFY_CUSTOMER_ID
} from '#types/SignifyApi.types';
import { indexSignifyProductPrices } from '#types';
import { FieldTypeContext } from '../../../../../providers/FieldTypeContext';
import { MessageContext } from '../../../../../providers/MessageContext';
import { useSignifyDistributors, useSignifyProductSearch, useSignifyProductPriceAndAvailability } from '../../../api';
import { useSignifyMappings } from '../../../useSignifyMappings';
import { useSignifyProductFields } from '../../../useSignifyProductFields';
import { resolveSignifySourceMappings } from '../../../resolveSignifySourceMappings';
import { useCreateLineItemFromSignify } from './useCreateLineItemFromSignify';
import { useSignifyMappingValidation } from './useSignifyMappingValidation';
import { resolveSignifyRecordName, type SignifyRecordName } from './services';

interface UseSignifyProductModalProps {
  tableColumns?: TableColumnType[];
  onSuccess?: (entityRecordId: string) => void;
  onManualInsert?: (recordName: string, fields: Fields, entityReferences: EntityRecord['entityReferences']) => void;
}

/**
 * Main hook for managing the Signify product selection modal
 * Handles distributor selection, product search, field mapping, validation, and record creation
 * @param props - Configuration including table columns and success callback
 * @returns Modal state, handlers, and data for rendering the modal
 */
export const useSignifyProductModal = ({
  onSuccess,
  onManualInsert,
}: UseSignifyProductModalProps = {}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastDistributorId, setLastDistributorId] = useState<string>('');
  const [selectedDistributor, setSelectedDistributor] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<ProductSearchResult | null>(null);
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  const [typedDistributorSearch, setTypedDistributorSearch] = useState<string | null>(null);
  const [customMappings, setCustomMappings] = useState<Record<string, string | null>>({});
  const [saveRecord, setSaveRecord] = useState(false);

  const { fieldTypes } = useContext(FieldTypeContext);
  const { messageApi } = useContext(MessageContext);
  const { mappings: signifyMappings, configDisabledReason } = useSignifyMappings();
  const { availableProductFields, productsEntity } = useSignifyProductFields(true);

  const {
    distributors: filteredDistributors,
    allDistributors,
    loading: loadingDistributors,
    error: distributorError,
    searchTerm: distributorSearchTerm,
    setSearchTerm: setDistributorSearchTerm,
    refreshDistributors,
    loadDistributorsIfNeeded,
    totalCount: totalDistributors,
    filteredCount: filteredDistributorsCount,
  } = useSignifyDistributors(DEFAULT_SIGNIFY_CUSTOMER_ID);

  const {
    productOptions,
    searchingProducts,
    searchProducts,
    clearSearch,
  } = useSignifyProductSearch();

  const {
    loading: loadingPrice,
    error: priceError,
    productPriceAndAvailabilityData,
    fetchProductPriceAndAvailability,
    clearProductPriceAndAvailability,
  } = useSignifyProductPriceAndAvailability();

  const { createLineItemRecord, buildLineItemFields } = useCreateLineItemFromSignify();

  const error = priceError || distributorError;

  const distributorSearchValue = typedDistributorSearch
    ?? allDistributors.find((d) => d.uid === selectedDistributor)?.name
    ?? '';

  /**
   * Gets the default distributor from the loaded distributors list
   */
  const getDefaultDistributor = useCallback(() => {
    return allDistributors.find(d => d.uid === DEFAULT_SIGNIFY_CUSTOMER_ID);
  }, [allDistributors]);

  /**
   * Auto-populates the default distributor if none is selected
   */
  const autoPopulateDefaultDistributor = useCallback(() => {
    if (!selectedDistributor) {
      const defaultDistributor = getDefaultDistributor();
      if (defaultDistributor) {
        setSelectedDistributor(defaultDistributor.uid);
        setTypedDistributorSearch(null);
        setLastDistributorId(defaultDistributor.uid);
        return defaultDistributor;
      }
    }
    return null;
  }, [selectedDistributor, getDefaultDistributor]);

  useEffect(() => {
    if (isModalOpen) {
      loadDistributorsIfNeeded();
    }
  }, [isModalOpen, loadDistributorsIfNeeded]);

  const { validateSingleMapping, sourceTypesMap, destinationFieldsMap } =
    useSignifyMappingValidation({
      productPriceAndAvailabilityData,
      availableLineItemFields: availableProductFields
    });

  /**
   * Resolves the configured rules against this response and presents them
   * source-first, filtered to destinations that exist on the Products entity.
   * Resolution is response-dependent: `overageBasePrice` binds to
   * `price.prices.TARGET_PRICE` or `price.prices.CUSTOMER_PRICE` per item, and
   * `sourceRules` reports which rule matched and the fallback list it listed.
   */
  const resolvedSourceMappings = useMemo(() => resolveSignifySourceMappings({
    fieldMappings: signifyMappings.fieldMappings,
    response: productPriceAndAvailabilityData
      ? indexSignifyProductPrices(productPriceAndAvailabilityData)
      : {},
    availableFieldIds: availableProductFields.map(f => f.fieldId),
  }), [signifyMappings, productPriceAndAvailabilityData, availableProductFields]);

  /**
   * Gets display names for LineItem fields without type suffix.
   * Type information is now shown in tooltips instead.
   */
  const getColumnDisplayName = useCallback(
    (fieldId: string) => {
      const field = availableProductFields.find(f => f.fieldId === fieldId);
      if (field) {
        return field.label;
      }
      const ft = fieldTypes[fieldId];
      return ft?.description?.shortLabel || fieldId;
    },
    [availableProductFields, fieldTypes]
  );

  /**
   * Track duplicate mappings (two response paths pointing at one Line Item
   * field). Configured rules cannot produce one — each destination resolves
   * once — so this only ever fires on a session override, where it is a real
   * conflict: whichever write runs last silently wins.
   * @returns Record of Line Item fields that have multiple API fields mapped to them
   */
  const duplicateMappings = useMemo(() => {
    const usage: Record<string, string[]> = {};
    const allMappings: Record<string, string | null> = {
      ...resolvedSourceMappings.sourceMappings,
      ...customMappings
    };

    Object.entries(allMappings).forEach(([apiField, lineItemField]) => {
      if (!lineItemField || lineItemField === null) return;
      if (!usage[lineItemField]) usage[lineItemField] = [];
      usage[lineItemField].push(apiField);
    });

    return Object.entries(usage)
      .filter(([_, apiFields]) => apiFields.length > 1)
      .reduce((acc, [lineItemField, apiFields]) => {
        acc[lineItemField] = apiFields;
        return acc;
      }, {} as Record<string, string[]>);
  }, [resolvedSourceMappings, customMappings]);

  /**
   * Validate all current mappings for type compatibility
   * Checks that required fields exist and all mappings have compatible source/destination types
   * @returns Record of validation results keyed by Line Item field ID
   */
  const mappingValidations = useMemo(() => {
    const validations: Record<string, { isValid: boolean; errorMessage: string | null }> = {};

    if (!productPriceAndAvailabilityData) return validations;

    // Check for required product.code field
    const hasProductCode = sourceTypesMap.has('product.code');
    if (!hasProductCode) {
      validations['manufacturersProductId'] = {
        isValid: false,
        errorMessage: 'Required field "product.code" not found in API data - cannot create record'
      };
    }

    const allMappings: Record<string, string | null> = {
      ...resolvedSourceMappings.sourceMappings,
      ...customMappings
    };

    Object.entries(allMappings).forEach(([apiField, lineItemField]) => {
      if (!lineItemField || lineItemField === null) return;
      
      // Only validate if the source field exists in the API data
      // (All mappings except product.code are optional)
      const sourceExists = sourceTypesMap.has(apiField);
      if (!sourceExists) {
        // If source doesn't exist, it's not an error - just skip it
        // This is fine because optional fields can be missing
        return;
      }
      
      const validation = validateSingleMapping(apiField, lineItemField);
      validations[lineItemField] = validation;
    });

    return validations;
  }, [productPriceAndAvailabilityData, resolvedSourceMappings, customMappings, validateSingleMapping, sourceTypesMap]);

  /**
   * Check if all mappings are valid (no validation errors and no duplicates)
   * Used to control Insert button state and show validation warnings
   * @returns True if there are any validation errors or duplicate mappings
   */
  const hasValidationErrors = useMemo(() => {
    const invalidMappings = Object.entries(mappingValidations).filter(([, v]) => !v.isValid);
    const duplicateEntries = Object.entries(duplicateMappings);

    if (invalidMappings.length > 0) {
      console.error('[Signify] Mapping validation errors:', invalidMappings.map(([field, v]) => `${field}: ${v.errorMessage}`));
    }
    if (duplicateEntries.length > 0) {
      console.error('[Signify] Duplicate mappings:', duplicateEntries.map(([field, sources]) => `${field} ← ${sources.join(', ')}`));
    }

    return invalidMappings.length > 0 || duplicateEntries.length > 0;
  }, [mappingValidations, duplicateMappings]);

  /**
   * Opens the modal
   */
  const openModal = useCallback(() => {
    if (!selectedDistributor) {
      if (lastDistributorId) {
        setSelectedDistributor(lastDistributorId);
      } else {
        setSelectedDistributor(DEFAULT_SIGNIFY_CUSTOMER_ID);
        setLastDistributorId(DEFAULT_SIGNIFY_CUSTOMER_ID);
      }
      setTypedDistributorSearch(null);
    }
    setIsModalOpen(true);
  }, [lastDistributorId, selectedDistributor]);

  /**
   * Closes the modal and resets state
   */
  const closeModal = useCallback(() => {
    setSelectedProduct(null);
    setProductSearchValue('');
    setCustomMappings({});
    clearSearch();
    clearProductPriceAndAvailability();
    setIsModalOpen(false);
  }, [clearSearch, clearProductPriceAndAvailability]);

  /**
   * Executes a product search as the user types.
   */
  const onProductSearch = useCallback((value: string) => {
    setProductSearchValue(value);
    searchProducts(value);
  }, [searchProducts]);

  /**
   * Sets the selected product and fetches price if a distributor is already selected.
   * Auto-populates default distributor if none is selected.
   */
  const onProductSelect = useCallback(async (value: string) => {
    const product = productOptions.find((p) => p.code === value);
    if (product) {
      setSelectedProduct(product);
      setProductSearchValue(`${product.name} (${product.commercialCode})`);

      let distributorToUse = selectedDistributor;
      if (!distributorToUse) {
        const defaultDistributor = autoPopulateDefaultDistributor();
        if (defaultDistributor) {
          distributorToUse = defaultDistributor.uid;
        }
      }

      if (distributorToUse) {
        try {
          await fetchProductPriceAndAvailability(product.code, 'FULL', DEFAULT_SIGNIFY_CUSTOMER_ID, distributorToUse);
        } catch (err) {
          void err;
        }
      }
    }
  }, [productOptions, selectedDistributor, fetchProductPriceAndAvailability, autoPopulateDefaultDistributor]);

  /**
   * Clears product selection
   */
  const onProductClear = useCallback(() => {
    setSelectedProduct(null);
    setProductSearchValue('');
    clearProductPriceAndAvailability();
  }, [clearProductPriceAndAvailability]);

  /**
   * Updates the distributor search term.
   */
  const onDistributorSearch = useCallback((value: string) => {
    setTypedDistributorSearch(value);
    setDistributorSearchTerm(value);
  }, [setDistributorSearchTerm]);

  /**
   * Sets the selected distributor and fetches price if a product is already selected.
   */
  const onDistributorSelect = useCallback(async (value: string) => {
    setSelectedDistributor(value);
    const distributor = allDistributors.find((d) => d.uid === value);
    if (distributor) {
      setTypedDistributorSearch(null);
      setLastDistributorId(value);

      if (selectedProduct) {
        try {
          await fetchProductPriceAndAvailability(selectedProduct.code, 'FULL', DEFAULT_SIGNIFY_CUSTOMER_ID, value);
        } catch (err) {
          void err;
        }
      }
    }
  }, [allDistributors, selectedProduct, fetchProductPriceAndAvailability]);

  /**
   * Clears distributor selection
   */
  const onDistributorClear = useCallback(() => {
    setSelectedDistributor('');
    setTypedDistributorSearch(null);
    setLastDistributorId('');
    clearProductPriceAndAvailability();
  }, [clearProductPriceAndAvailability]);

  /**
   * Handles mapping changes between API fields and table columns.
   */
  const onMappingChange = useCallback(
    (apiField: string, tableColumn: string | null) => {
      const originalMapping = resolvedSourceMappings.sourceMappings[apiField];

      if (tableColumn) {
        setCustomMappings((prev) => ({
          ...prev,
          [apiField]: tableColumn,
        }));
      } else {
        if (originalMapping) {
          setCustomMappings((prev) => ({
            ...prev,
            [apiField]: null,
          }));
        } else {
          setCustomMappings((prev) => {
            const updated = { ...prev };
            delete updated[apiField];
            return updated;
          });
        }
      }
    },
    [resolvedSourceMappings]
  );

  /**
   * Creates or updates a Products entity record from Signify data and passes the record ID to the parent.
   * Auto-populates default distributor if needed.
   * Displays appropriate message based on upsert status.
   */
  const onSaveRecordChange = useCallback((value: boolean) => {
    setSaveRecord(value);
  }, []);

  const onInsertIntoTable = useCallback(async () => {
    let distributorToUse = selectedDistributor;
    if (!distributorToUse) {
      const defaultDistributor = autoPopulateDefaultDistributor();
      if (defaultDistributor) {
        distributorToUse = defaultDistributor.uid;
      }
    }

    if (!distributorToUse || !selectedProduct || !productPriceAndAvailabilityData) {
      return;
    }

    try {
      /**
       * The rows render from `productPriceAndAvailabilityData` and the record is
       * written from the same object, so the memo is already the inversion of the
       * response being written and needs no second pass.
       */
      const sessionMappings = {
        customMappings,
        surfacedDestinations: resolvedSourceMappings.sourceMappings,
      };

      if (saveRecord && onSuccess) {
        const { recordId, status } = await createLineItemRecord(productPriceAndAvailabilityData, sessionMappings);

        if (status === 'duplicate') {
          messageApi.warning('Multiple Line Items found with this product ID - please review your records');
        }

        onSuccess(recordId);
      } else if (onManualInsert) {
        const { fields, recordName, entityReferences } = await buildLineItemFields(productPriceAndAvailabilityData, sessionMappings);
        onManualInsert(recordName, fields, entityReferences);
      }

      closeModal();
    } catch (error) {
      if (error instanceof Error && error.message.includes('Validation failed')) {
        console.error('Signify data validation failed:', error.message);
        messageApi.error('Validation failed - please check field mappings');
      } else {
        console.error('Failed to create LineItem record from Signify data:', error);
        messageApi.error('Failed to save Line Item');
      }
      throw error;
    }
  }, [
    selectedDistributor,
    selectedProduct,
    productPriceAndAvailabilityData,
    customMappings,
    resolvedSourceMappings,
    saveRecord,
    onSuccess,
    onManualInsert,
    closeModal,
    autoPopulateDefaultDistributor,
    createLineItemRecord,
    buildLineItemFields,
    messageApi,
  ]);

  /**
   * Previews the name the created record will take, resolved through the same
   * rules and text fallback the builder uses, so a brand condition on the
   * Record Name destination shows here before insert. Null only while no
   * product is loaded: a product the rules cannot name reports which of the two
   * failures `useCreateLineItemFromSignify` will throw on, rather than reading
   * as no name at all.
   */
  const recordNameInfo = useMemo<SignifyRecordName | null>(() => {
    if (!productPriceAndAvailabilityData) return null;

    return resolveSignifyRecordName({
      fieldMappings: signifyMappings.fieldMappings,
      response: indexSignifyProductPrices(productPriceAndAvailabilityData)
    });
  }, [productPriceAndAvailabilityData, signifyMappings]);

  /**
   * Check if the modal footer should be shown
   * Footer shows when we have a product (distributor can be auto-populated)
   * @returns True if footer should be displayed
   */
  const shouldShowFooter = !!(selectedProduct && (selectedDistributor || getDefaultDistributor()));

  return {
    openModal,
    open: isModalOpen,
    onCancel: closeModal,
    selectedDistributor,
    selectedProduct,
    productSearchValue,
    distributorSearchValue,
    filteredDistributors,
    filteredDistributorsCount,
    totalDistributors,
    distributorSearchTerm,
    loadingDistributors,
    productOptions,
    searchingProducts,
    productPriceAndAvailabilityData,
    loadingPrice,
    sourceMappings: resolvedSourceMappings.sourceMappings,
    sourceRules: resolvedSourceMappings.sourceRules,
    constantMappings: resolvedSourceMappings.constants,
    customMappings,
    availableTableColumns: availableProductFields.map(f => f.fieldId),
    duplicateMappings,
    validateSingleMapping,
    sourceTypesMap,
    destinationFieldsMap,
    hasValidationErrors,
    recordNameInfo,
    destinationEntityLabel: productsEntity?.description?.longLabel,
    configDisabledReason,
    error,
    onProductSearch,
    onProductSelect,
    onProductClear,
    onDistributorSearch,
    onDistributorSelect,
    onDistributorClear,
    onMappingChange,
    onInsertIntoTable,
    onRefreshDistributors: refreshDistributors,
    getColumnDisplayName,
    shouldShowFooter,
    saveRecord,
    onSaveRecordChange,
  };
};